package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.snapshotFlow
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/snapshot_flow")
@Composable
fun SnapshotFlowPage() {
    BlogLayout(
        PageProperties(
            "snapshotFlow",
            "snapshotFlow (Transform (Bridge) Compose State into Flows - Bridge between Compose State and Kotlin Flows - Flow operators (like map, filter, debounce, sorting)) - Avoid (Heavy Computation, Network Calls, State Mutation)"
        ),
        BlogProperties("Jetpack Compose", "snapshotFlow"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "The Problem: Bridging Compose State to Kotlin Coroutines Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose provides reactive snapshot state, while backend utilities, repositories, and reactive business logic often expect cold Kotlin Flow streams with operators like .debounce(), .filter(), and .distinctUntilChanged().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "The Solution",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow observes Compose state reads within a block and converts them into a cold Flow. It emits the initial value, tracks read dependencies, and re-emits downstream only when read states change to a value distinct from the prior emission.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "snapshotFlow produces a stream of values you consume in a coroutine. snapshotFlow (Transform (Bridge) Compose State into Flows - Bridge between Compose State and Kotlin Flows - Flow operators (like map, filter, debounce, sorting, pagination)) - Avoid (Heavy Computation, Network Calls, State Mutation)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SnapshotFlow lets you convert state reads inside Compose into a Flow, so you can collect and respond to state changes reactively (snapshotFlow bridges Compose state (Snapshot system) to Kotlin Flows). It tracks state reads, not just variables. observes reads of Compose state inside its block and emits a new value to the Flow whenever any of those observed states change. It runs in a coroutine, respects lifecycle when collected (e.g., in LaunchedEffect), applies Flow operators, and avoids recomposition-driven side effects by moving them to a structured, cancellable stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow converts Compose states (e.g., LazyListState, derivedStateOf) into a cold Flow, allowing you to react to state changes without unnecessary recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You can use snapshotFlow to convert a State<T> object to a cold Flow. snapshotFlow executes the parameter block when it starts collecting and emits the results of the executed State object. If one of the State objects read inside the snapshotFlow block changes and the new value is not the same as the previously emitted value, the Flow may emit a new value to the collector (similar to Flow.distinctUntilChanged).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SnapshotFlow is used to calculate the difference between the previous and current state using flows. It is a helper class provided by Compose and can be used to create flows that monitor state objects. When subscribing to a flow, SnapshotFlow takes a snapshot of the current state and then watches for any changes in that state. If the state changes, it takes a new snapshot and calculates the difference between the new and previous snapshots.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow is a function that enables you to create a flow that initially emits the current value of a state object and then continues to emit any changes to that object. This is helpful for building reactive UIs that automatically respond to state changes, eliminating the need for manual callbacks or listeners.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is used to convert Compose state objects into a cold Flow. Thanks to this flow transformation, the help of flow operators can be used on the state. It allows you to observe changes to Compose state values in a coroutine-based Flow format, which can then be collected and transformed asynchronously.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Useful for exposing Composable state as a Flow so that it can be observed and reacted to by external components. This compose function is used to convert a compose state into a flow that emits values whenever a compose state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow defined in ViewModel was changed to Flow -> State by calling Flow.collectAsState() to use in Compose, this API plays the role of changing State -> Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The reason for converting to Flow is that more efficient code can be configured using various operators supported by Flow. Analysis value: Get old and previous value, debounce, throttle, filter, add or even",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Important notes",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Only state read inside the lambda is tracked.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It coalesces rapid changes; use operators like distinctUntilChanged if needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The block must be side-effect free (just read state, return value).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Key Characteristics of snapshotFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Observation: It listens to Compose’s state changes using the Snapshot system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Thread Safety: It ensures that state reads and emissions happen within Compose’s snapshot isolation, avoiding race conditions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cancelation-Aware: It automatically cancels observation when the coroutine collecting the Flow is canceled, making it composition-aware.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Idle Skipping: It ensures emissions happen only when the state value changes and skips updates during idle recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Execution Lifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Read Tracking: The SnapshotStateObserver registers every state object accessed inside the block as a dependency (tracked as a set, not a full graph).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Invalidation: When a dependency is updated and the snapshot is committed, the observer is notified.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Selective Emission: The block re-runs. A new value is emitted only if it differs from the previous value (newValue != oldValue) using structural equality.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "snapshotFlow Internal working?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Track any state reads inside the lambda you provide within a Snapshot observer. If the lambda accesses any state variables, Compose registers a dependency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When that state changes → When the state changes, Compose notifies the snapshotFlow, triggering it to emit the new value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The coroutine collecting the Flow receives updates",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Avoid the snapshotFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Deriving Ul state for other Composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Performing simple state transformations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You are inside a Composable only.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You need immediate Ul recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When to Use snapshotFlow?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Interfacing with Coroutines: When you need to bridge Compose’s state with coroutine flows for advanced operations like transformations, combining flows, or throttling updates.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Non-UI Side Effects: To perform tasks that shouldn’t directly tie to the UI, such as sending analytics events or triggering backend calls.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Reacting to Compose states (scroll, gestures, animations).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sending Analytics/Logging events",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Bridging to Flow-based APIs (Room, DataStore)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Transform UI state with Flow operators.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Combine Compose state with other Flow sources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Triggering Navigation based on state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Observing high-frequency gestures/scrolls.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Triggering side-effects without causing recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Building Flow pipelines from Compose states (analytics, lazy loading triggers).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Pagination in LazyColumn.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Search with debounce on Ul state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow: Suppose you want to listen to changes in a Compose State, such as changing the scroll position or the size of a component. Compose itself manages the State, but if we want to capture these changes as a Flow, we need to use snapshotFlow. Every time the first item in the list changes, we get it into the Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This method is mostly used to display Flow data directly in the UI. That is, when the ViewModel returns a Flow, we want to have it as a State in Compose. Every time the Flow data changes, the UI will be automatically refreshed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "snapshotFlow with Coroutines Operators?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //debounce
                    snapshotFlow { query }.debounce(600)
                    
                    //Throttle
                    snapshotFlow { scrollOffset }.sample(600)
                    
                    //Distinct
                    snapshotFlow { uiState }.distinctUntilChanged()
                    
                    //Convert to SharedFlow
                    val shared = snapshotFlow { count }.shareIn(scope, SharingStarted.Lazily)
                    """.trimIndent()
            )

            SpanText(
                text = "Why do we need to wrap SnapshotFlow under Launched Effect?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Because snapshotFlow is a Flow, and collecting a Flow requires a coroutine, and Jetpack Compose does not automatically provide a coroutine inside normal Composable Functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect gives us:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A lifecycle-aware coroutine scope.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "That is tied to the composition.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "So our snapshotFlow collects safely without leaks.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "How snapshotFlow Helps in Clean Architecture?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel: Holds business logic and exposes StateFlows.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI (Compose): Converts UI state →Flow using snapshotFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Benefit: UI does not push extra logic into the ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "snapshotFlow vs. The Alternatives",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow: Converts Compose State → Flow. Best for side effects outside the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf: Keeps logic within Compose. Best for minimizing recompositions when one state depends on another.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsState: Converts Flow → Compose State. The reverse of snapshotFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "snapshotFlow Things to Keep in Mind",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Thread Safety and Collection: Ensure that the collected flow is handled within a coroutine scope, typically using LaunchedEffect, to avoid collecting outside the composition and prevent memory leaks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Emission Frequency: snapshotFlow emits values whenever a snapshot state read inside its lambda changes, which can happen frequently. Use operators like distinctUntilChanged() or debounce() to reduce unnecessary emissions and optimize performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Snapshot Isolation: The flow respects Compose’s snapshot system, meaning values are emitted based on isolated snapshots. Be cautious when combining with other flows or suspending functions to ensure expected behavior within the snapshot lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is snapshotFlow in Jetpack Compose and how does it work?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is a utility that allows you to observe changes in Compose State and transform them into a cold Kotlin Flow. It works by registering a read observer on the snapshot system, re-running the block when dependencies change, and emitting values only when they change structurally.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Does snapshotFlow recompose UI?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No. snapshotFlow itself does not trigger recomposition. It runs in a coroutine and observes state changes independently of the UI drawing cycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Is snapshotFlow expensive?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It has a minor overhead for dependency tracking and re-execution. However, because it is a cold Flow, it only consumes resources while it is being collected.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //If you return a MutableList and modify it in place, why won't snapshotFlow emit a new value?
            //Why is it dangerous to perform a network call directly inside the snapshotFlow block?
            //If you are tracking scrollOffset for a parallax effect, would you use .sample(16.ms) or .debounce(100.ms) to ensure the UI doesn't stutter?

            SpanText(
                text = "Critical Anti-Patterns",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "\uD83D\uDEAB Heavy Computation: Never put expensive logic inside the snapshotFlow block; it re-runs on every invalidation.\n",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "\uD83D\uDEAB Network Calls: Do not trigger API calls inside the block. The block must be pure. Trigger the call in the collect phase instead. snapshotFlow can emit very frequently, and putting a network call directly inside it leads to uncontrolled side effects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "\uD83D\uDEAB State Mutation: Never update a tracked state variable inside its own snapshotFlow block. This creates an infinite invalidation loop.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "snapshotFlow Summary",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SnapshotFlow is a Jetpack Compose API that converts Compose state reads into a Kotlin Flow. Whenever a state changes, it emits the new value through the Flow. This allows us to react to UI state changes using coroutines instead of triggering recomposition. It’s ideal for tasks like debounced search, pagination, analytics, scroll tracking, and any side effects that shouldn’t run inside the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Pagination",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun StopBoardPanel(
                        isLoading: Boolean = false,
                        isFailed: Boolean = false,
                        isOnline: Boolean = true,
                        canLoadMore: Boolean = false,
                        isLoadingMore: Boolean = false,
                        onFilterOut: (String) -> Unit = {},
                        onSearchFurtherAhead: () -> Unit = {},
                        onRetry: () -> Unit = {},
                        onLoadMore: () -> Unit = {},
                    ) {
                        val listState = rememberLazyListState()
                        // Only the user's scrolling asks for a page. A list that fits the panel never
                        // does: a single line folds every page into the same row, so it would keep
                        // fitting and keep asking. Nor may a board left at its end on the wall grow on
                        // every auto-refresh, which restarts this effect.
                        LaunchedEffect(listState, canLoadMore, isLoading, isLoadingMore) {
                            if (!canLoadMore || isLoading || isLoadingMore) return@LaunchedEffect
                            snapshotFlow {
                                val info = listState.layoutInfo
                                // Empty until the list is first measured, which is not "at the end".
                                val last = info.visibleItemsInfo.lastOrNull() ?: return@snapshotFlow false
                                val nearEnd = last.index >= info.totalItemsCount - LOAD_MORE_THRESHOLD
                                nearEnd && listState.isScrollInProgress
                            }.first { it }
                            onLoadMore()
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Pagination call
                    
                    val scrollState = rememberScrollState()
                    val isAtBottom by snapshotFlow {
                        val max = scrollState.maxValue.toFloat()
                        max > 0f && scrollState.value >= max
                    }.collectAsStateWithLifecycle(false)

                    LaunchedEffect (isAtBottom) {
                        if (isAtBottom) {
                            loadmore()
                        }
                    }
                """.trimIndent()
            )

            //https://github.com/kalaimscse1/resB-multiplatform
            KotlinCode(
                code = """
                    /**
                     * Smoothly scrolls the list to the bottom.
                     */
                     
                    addItem = {
                        scope.scrollToBottomSmooth(listState) // 👈 reusable
                    }
                    
                    removeItem = {
                        scope.scrollToBottomSmooth(listState) // 👈 reusable
                    }
                    
                    fun CoroutineScope.scrollToBottomSmooth(listState: LazyListState) {
                        this.launch {
                            val total = listState.layoutInfo.totalItemsCount
                            if (total > 0) {
                                listState.animateScrollToItem(total - 1)
                            }
                        }
                    }

                    /**
                     * Keeps the last item visible above a BottomBar of given height.
                     */
                    @Composable
                    fun LazyListState.ensureLastItemVisible(bottomBarHeight: Dp) {
                        val scope = rememberCoroutineScope()
                        val bottomBarPx = with(LocalDensity.current) { bottomBarHeight.toPx() }

                        LaunchedEffect(this) {
                            snapshotFlow { layoutInfo }
                                .collect { layoutInfo ->
                                    val lastVisible = layoutInfo.visibleItemsInfo.lastOrNull()
                                    if (lastVisible != null) {
                                        val viewportEnd = layoutInfo.viewportEndOffset
                                        val itemEnd = lastVisible.offset + lastVisible.size

                                        if (itemEnd > viewportEnd - bottomBarPx) {
                                            scope.scrollToBottomSmooth(this@ensureLastItemVisible)
                                        }
                                    }
                                }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    val uiState by viewModel.uiState.collectAsState()
                    Text(text = uiState.message)
                    
                    val scrollState = rememberLazyListState(
                    
                    LaunchedEffect(Unit) {
                        snapshotFLow {scrollState.firstVisibleltemIndex)
                            .collect { index ->
                                Log.d("Scroll", "First visible item: $index")
                            }
                    }        
                    
                    """.trimIndent()
            )

            //https://www.linkedin.com/in/mdtayoburrahman
            KotlinCode(
                code = """
                    var searchText by remember { mutableStateOf("") }
                    
                    LaunchedEffect (Unit) {
                        snapshotFlow { searchText }
                        .debounce(300.milliseconds)
                        .filter { it.length > 1 }
                         //.distinctUntilChanged()
                        .collect { search ->
                           
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun SearchScreen()
                    {
                        var query by remember { (mutableStateOf("") }
                        val results = remember { mutableStateListOf<String>() }
                        val all = remember { listOf("Apple", "Apricot", "Banana", "Blueberry") }
                        
                        LaunchedEffect(Unit) {
                            snapShotFlow { query }
                                .map { it.trim() }
                                .distinctUntilChanged()
                                mapLatest { q ->
                                    if (q.isEmpty()) all else all.filter { it.contains(q, ignoreCase = true) }
                                }
                            }
                            .collect { list →
                                results.clear()
                                results.addAll(list)
                            }    
                        }
                    }
                    
                    Column {
                        TextField(value = query, onValueChange = { query = it }, label = { Text("Search") })
                        LazyColumn {
                            items(results) { Text(it) }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/xephosbot/AnilibriaRefresh
            KotlinCode(
                code = """
                    //Pagination with Search/Filter (snapshotFlow search)
                    
                    @OptIn(ExperimentalCoroutinesApi::class)
                    class SearchViewModel(
                    ) : ViewModel() {

                        val searchFieldState: TextFieldState = TextFieldState()
                        private val searchQuery: Flow<String> = snapshotFlow {
                            searchFieldState.text.toString()
                        }
                        
                        private val _filters: MutableStateFlow<SearchScreenState> = MutableStateFlow(SearchScreenState())
    
                        val filters: StateFlow<SearchScreenState> = _filters
                        .onStart { fetchCatalogFilters() }
                        .stateIn(
                            scope = viewModelScope,
                            started = SharingStarted.Lazily,
                            initialValue = _filters.value
                        )
                        
                        private fun fetchCatalogFilters() {
                            viewModelScope.launch { }
                        }

                        @OptIn(FlowPreview::class)
                        val searchResult: Flow<PagingData<Release>> = combine(
                            searchQuery.debounce(500L),
                            filters.debounce(500L)
                        ) { searchQuery, filters ->
                            searchQuery to filters
                          }
                          .flatMapLatest { (searchQuery, filters) ->
           
                          }
                          .cachedIn(viewModelScope)
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun TopSearchInputField(
                        state: TextFieldState,
                        onSearch: (String) -> Unit,
                        modifier: Modifier = Modifier,
                        enabled: Boolean = true,
                        readOnly: Boolean = false,
                        textStyle: TextStyle = LocalTextStyle.current,
                        placeholder: @Composable (() -> Unit)? = null,
                        leadingIcon: @Composable (() -> Unit)? = null,
                        trailingIcon: @Composable (() -> Unit)? = null,
                        prefix: @Composable (() -> Unit)? = null,
                        suffix: @Composable (() -> Unit)? = null,
                        inputTransformation: InputTransformation? = null,
                        outputTransformation: OutputTransformation? = null,
                        scrollState: ScrollState = rememberScrollState(),
                        colors: TextFieldColors = inputFieldColors(),
                        windowInsets: WindowInsets = SearchBarDefaults.windowInsets,
                        interactionSource: MutableInteractionSource? = null
                    ) {
                        @Suppress("NAME_SHADOWING")
                        val interactionSource = interactionSource ?: remember { MutableInteractionSource() }

                        val focused = interactionSource.collectIsFocusedAsState().value
                        val focusRequester = remember { FocusRequester() }

                        val textColor =
                            textStyle.color.takeOrElse {
                                colors.textColor(enabled, isError = false, focused = focused)
                            }
                        val mergedTextStyle = textStyle.merge(TextStyle(color = textColor))

                        LaunchedEffect(Unit) { focusRequester.requestFocus() }

                        BasicTextField(
                            state = state,
                            modifier = modifier
                                .sizeIn(minHeight = InputFieldHeight)
                                .focusRequester(focusRequester)
                                .windowInsetsPadding(windowInsets),
                            enabled = enabled,
                            readOnly = readOnly,
                            lineLimits = TextFieldLineLimits.SingleLine,
                            textStyle = mergedTextStyle,
                            cursorBrush = SolidColor(colors.cursorColor(isError = false)),
                            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                            onKeyboardAction = { onSearch(state.text.toString()) },
                            interactionSource = interactionSource,
                            inputTransformation = inputTransformation,
                            outputTransformation = outputTransformation,
                            scrollState = scrollState,
                            decorator = TextFieldDefaults.decorator(
                                state = state,
                                enabled = enabled,
                                lineLimits = TextFieldLineLimits.SingleLine,
                                outputTransformation = outputTransformation,
                                interactionSource = interactionSource,
                                placeholder = placeholder,
                                leadingIcon =
                                leadingIcon?.let { leading ->
                                    { Box(Modifier.offset(x = SearchBarIconOffsetX)) { leading() } }
                                },
                                trailingIcon =
                                trailingIcon?.let { trailing ->
                                    { Box(Modifier.offset(x = -SearchBarIconOffsetX)) { trailing() } }
                                },
                                prefix = prefix,
                                suffix = suffix,
                                colors = colors,
                                contentPadding = TextFieldDefaults.contentPaddingWithoutLabel(),
                                container = {
                                    Box(modifier = Modifier.fillMaxSize())
                                }
                            )
                        )
                    }

                    private val SearchBarIconOffsetX: Dp = 4.dp
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val progress by snapshotFlow { animationState.progress }
                        .collectAsState(initial = 0f)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    LaunchedEffect(Unit) {
                        snapshotFlow { animationState.progress }
                            .distinctUntilChanged { old, new ->
                                (old * 100).toInt() == (new * 100).toInt()
                            }
                            .collect { progress ->
                                analytics.logAnimationProgress(progress)
                            }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val listState = rememberLazyListState()

                    LaunchedEffect(Unit) {
                        snapshotFlow { listState.firstVisibleItemIndex }
                            .distinctUntilChanged()
                            .collect { index ->
                                analytics.logScrollPosition(index)
                            }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    fun App() {
                        MaterialTheme {
                            var showMenu by remember { mutableStateOf(false) }
                            var selectedItem by remember { mutableStateOf(0) }

                            Scaffold(
                                topBar = {
                                    TopAppBar(
                                        title = { Text("Material 3 Scaffold Sample") },
                                        actions = {
                                            val buttonFocusRequester = remember { FocusRequester() }
                                            IconButton(
                                                onClick = { showMenu = !showMenu },
                                                modifier = Modifier.focusRequester(buttonFocusRequester)
                                            ) {
                                                Icon(Icons.Filled.MoreVert, contentDescription = "More")
                                            }

                                            DropdownMenu(
                                                expanded = showMenu,
                                                onDismissRequest = {
                                                    showMenu = false
                                                },
                                            ) {
                                                val items = List(5) { "Menu Item $it" }
                                                val focusRequesters: List<FocusRequester> = remember(items) {
                                                    items.map { FocusRequester() }
                                                }

                                                items.forEachIndexed { idx, item ->
                                                    DropdownMenuItem(
                                                        text = { Text(item) },
                                                        onClick = {
                                                            println("Clicked on $item")
                                                            showMenu = false
                                                        },
                                                        modifier = Modifier
                                                            .focusRequester(focusRequesters[idx])
                                                            .focusProperties {
                                                                // Focus properties are not respected
                                                                next = focusRequesters[(idx + 1) % items.size]
                                                                previous = focusRequesters[(idx - 1 + items.size) % items.size]

                                                                //right = focusRequesters[(idx + 1) % items.size]
                                                                //left = focusRequesters[(idx - 1 + items.size) % items.size]
                                                            }
                                                    )
                                                }

                                                val windowInfo = LocalWindowInfo.current
                                                LaunchedEffect(Unit) {
                                                    snapshotFlow { windowInfo.isWindowFocused }.collect { isWindowFocused ->
                                                        if (isWindowFocused) {
                                                            focusRequesters.first().requestFocus()
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    )
                                },
                                
                                bottomBar = {
                                    NavigationBar {
                                        NavigationBarItem(
                                            selected = selectedItem == 0,
                                            onClick = {
                                                selectedItem = 0
                                                println("Clicked on Home")
                                            },
                                            label = { Text("Home") },
                                            icon = { Icon(Icons.Filled.Home, contentDescription = "Home") }
                                        )
                                        NavigationBarItem(
                                            selected = selectedItem == 1,
                                            onClick = {
                                                selectedItem = 1
                                                println("Clicked on Search")
                                            },
                                            label = { Text("Search") },
                                            icon = { Icon(Icons.Filled.Search, contentDescription = "Search") }
                                        )
                                        NavigationBarItem(
                                            selected = selectedItem == 2,
                                            onClick = {
                                                selectedItem = 2
                                                println("Clicked on Settings")
                                            },
                                            label = { Text("Settings") },
                                            icon = { Icon(Icons.Filled.Settings, contentDescription = "Settings") }
                                        )
                                    }
                                }
                            ) { paddingValues ->

                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ColorPicker(colors: List<Color>) {
                        val selectedColor = remember { mutableStateOf(colors.first()) }

                        val selectedColorFlow = snapshotFlow { selectedColor.value }

                        val lastSelectedColor = selectedColorFlow.collectAsState(selectedColor.value)

                        Box(
                            Modifier
                                .fillMaxSize()
                                .background(lastSelectedColor.value)
                                .clickable {
                                    val index = (colors.indexOf(lastSelectedColor.value) + 1) % colors.size
                                    selectedColor.value = colors[index]
                                }
                        ) {
                            Text("Click to change color", modifier = Modifier.align(Alignment.Center))
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //pull-to-refresh 
                    
                    fun NotesScreen(viewModel: NotesViewModel = hiltViewModel()) {
                        val state by viewModel.state.collectAsState()
                        val scope = rememberCoroutineScope()
                        val snackbarHostState = remember { SnackbarHostState() }

                        PullRefreshIndicator(
                            refreshing = state.isRefreshing,
                            onRefresh = {
                                scope.launch {
                                    viewModel.refreshNotes()  // manual launch
                                }
                            }
                        )

                        LazyColumn {
                            items(state.notes) { note ->
                                NoteItem(
                                    note = note,
                                    onSave = { updatedNote ->
                                        scope.launch {
                                            val result = viewModel.updateNote(updatedNote)
                                            if (result.isSuccess) {
                                                snackbarHostState.showSnackbar("Note saved!")
                                            }
                                        }
                                    }
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/lerchenflo/SchneaggchatV3.git
            KotlinCode(
                code = """
                    //Sorting From Database TODO all List
                    
                    class TodolistViewModel(
                        private val todoRepository: TodoRepository
                    ): ViewModel() {
                        @OptIn(ExperimentalCoroutinesApi::class)
                        val todoFlow: Flow<List<TodoEntry>> = todoRepository
                            .getTodoItemsFlow()
                            // combine repo items with the current sort type (snapshotFlow observes the Compose mutableStateOf)
                            .combine(snapshotFlow { sortType.value }) { list, sort ->
                                // Filter according to the selected sort type
                                val filtered = when (sort) {

                                }

                                // then sort (keeps your existing sort by priority, then lastChanged)
                                filtered.sortedWith(
                                    compareBy<TodoEntry> { it.status == BugStatus.Finished.value } // unfinished (false) come first
                                        .thenByDescending { it.priority }                         // then by priority (high -> low)
                                        .thenByDescending { it.lastChanged }                      // optional: newest changed first
                                )

                            }
                            .flowOn(Dispatchers.Default)
                    }
                    
                    class TodoRepository(
                        private val database: AppDatabase
                    ) {
                        fun getTodoItemsFlow(): Flow<List<TodoEntry>> {
                            return database.todoListDao().getAllTodos().map { entityList ->
                                entityList.map { entity ->
                                    entity.toTodoEntry()
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //React When Checkbox or Switch Changes
                     LaunchedEffect(Unit) {
                        snapshotFlow { isChecked }
                            .collect { checked ->
                                viewModel.updatePreference(checked)
                            }
                    }
                    
                    //Observe Selected Tab in a TabRow
                    LaunchedEffect(Unit) {
                        snapshotFlow { selectedTabIndex }
                            .collect { index ->
                                viewModel.onTabSelected(index)
                            }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @Composable
                    fun SnapshotFlowDemo() {
                        var count by remember { mutableStateOf(0) }

                        //snapshotFlow collects state changes and triggers actions on change
                        LaunchedEffect(count) {
                            snapshotFlow { count } // Converts state to flow
                                .collect { newCount ->
                                    println("Count has changed to: $newCount")
                                }
                        }
                        Button(onClick = { count++ }) {
                            Text("Increase Count")
                        }
                        Text("Current count: $count")
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow { count } creates a Flow that emits the current value of count whenever it changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The collect function is used to observe state changes, and the side effect logic (in this case, logging) happens whenever the state is updated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "LaunchedEffect(searchQuery)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Triggers whenever the key (searchQuery) changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cancels and restarts the coroutine every time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No Flow operators like debounce or distinctUntilChanged.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Great for simple, one-off side effects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Example: updating a counter or saving a single setting.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Without snapshotFlow - triggers on every keystroke
                    
                    LaunchedEffect(key = searchQuery) {
                        viewModel.performSearch(searchQuery)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "snapshotFlow { searchQuery }",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )


            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Converts Compose state into a Kotlin Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Emits values continuously as the state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Works seamlessly with Flow operators (debounce, map, etc.).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Runs inside a single, long-lived coroutine - no restarts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Perfect for continuous, high-frequency updates (like search typing).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Example: live search queries, sliders, or scroll tracking.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    Composable
                    fun SearchBar(onSearch: (String) -> Unit) {
                        var query by remember { mutableStateOf("") }

                        // Use LaunchedEffect with snapshotFlow to observe query changes
                        LaunchedEffect(query) {
                            snapshotFlow { query }
                                .debounce(300) // Only emit the value if no other input arrives within 300ms
                                .filter { it.isNotEmpty() } // Only search for non-empty queries
                                .collect { searchQuery ->
                                    onSearch(searchQuery) // Perform search or API call
                                }
                        }

                        TextField(
                            value = query,
                            onValueChange = { query = it },
                            modifier = Modifier.fillMaxWidth(),
                            placeholder = { Text("Search...") }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun LocationTrackerScreen(context: Context, onLocationUpdate: (Location) -> Unit) {
                        var location by remember { mutableStateOf<Location?>(null) }
                        val locationManager = context.getSystemService(Context.LOCATION_SERVICE) as LocationManager

                        DisposableEffect(Unit) {
                            val locationListener = LocationListener { loc ->
                                location = loc
                            }

                            locationManager.requestLocationUpdates(
                                LocationManager.GPS_PROVIDER,
                                1000L,
                                1f,
                                locationListener
                            )

                            onDispose {
                                locationManager.removeUpdates(locationListener)
                            }
                        }

                        // Using SnapshotFlow to monitor location changes and filter updates
                        LaunchedEffect(location) {
                            snapshotFlow { location }
                                .filter { newLocation ->
                                    newLocation != null && hasSignificantChange(location, newLocation)
                                }
                                .collect { significantLocation ->
                                    significantLocation?.let { onLocationUpdate(it) }
                                }
                        }

                        location?.let {
                            Text("Current Location")
                        } ?: CircularProgressIndicator()
                    }

                    fun hasSignificantChange(oldLocation: Location?, newLocation: Location): Boolean {
                        if (oldLocation == null) return true
                        val distance = FloatArray(1)
                        Location.distanceBetween(
                            oldLocation.latitude, oldLocation.longitude,
                            newLocation.latitude, newLocation.longitude,
                            distance
                        )
                        return distance[0] > 50 // Only send update if the location changes by more than 50 meters
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    Composable
                    fun MyComposableExample(count: MutableStateOf<Int>) {
                      val countFlow = snapshotFlow { count }

                      LaunchedEffect(key1 = countFlow) {
                        println(countFlow.value)
                      }
                      Text(text = count.value)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Analytics Bridge (Scroll Tracking)
                    //Deduplicate high-frequency UI events before they hit your backend.
                    
                    val listState = rememberLazyListState()

                    LazyColumn(state = listState) {
                        // ...
                    }

                    LaunchedEffect(listState) {
                        snapshotFlow { listState.firstVisibleItemIndex }
                            .map { index -> index > 0 } // Only care if they passed item 0
                            .distinctUntilChanged()
                            .filter { it == true }
                            .collect {
                                MyAnalyticsService.sendScrolledPastFirstItemEvent()
                            }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ReceiptList(
                        showFloatingActionButton: () -> Unit
                    ) {
                        val lazyListState = rememberLazyListState()

                        LaunchedEffect(key1 = lazyListState) {
                            snapshotFlow { lazyListState.firstVisibleItemIndex }
                                .collect {
                                    if (it == 0) {
                                        showFloatingActionButton()
                                    }
                                }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //snapshotFlow: Converts count.value into a Flow.
                    //Flow operators: Use filtering and mapping to transform data.
                    //Parallel collection: Main flow and snapshotFlow run concurrently.

                    fun MyComponent() {
                        val list = listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11)
                        val count = remember { mutableStateOf(0) }
                        val countFlow = snapshotFlow { count.value }

                        val flow = flow {
                            list.forEach {
                                delay(1000)
                                emit(it)
                            }
                        }

                        LaunchedEffect(true) {
                            flow.collect {
                                count.value = it
                            }
                        }

                        LaunchedEffect(true) {
                            countFlow
                                .filter { it % 2 == 0 }
                                .map { "value is: $it" }
                                .collect { value ->
                                    Log.d("list item", value)
                                }
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://medium.com/@vikashkesharvani62/i-built-infinite-scroll-in-android-without-the-paging-library-heres-how-b88d4fe4a864
//https://medium.com/@arungohil10.ag/mastering-jetpack-compose-side-effect-states-7c5c71e56526